import {
  AttachMoneyOutlined,
  FactCheckOutlined,
  GavelOutlined,
  TextSnippetOutlined,
} from "@mui/icons-material";
import { Box, Card, Stack, Tooltip, Typography } from "@mui/material";
import React from "react";
import BellCurve from "../../../components/graphs/BellCurve";
import Donut from "../../../components/graphs/Donut";
import ProgressBar from "../../../components/graphs/ProgressBar";
import Dialog from "../../../components/misc/Dialog";
import { useCase } from "../../../contexts/CaseContext";
import Translations from "../../../lang/en/pages.json";
import { formatNumberWithSuffix } from "../../../utils";
import Likelihood from "./Likelihood";
import Button from "../../../components/buttons/Button";
import { components } from "../../../api/types";
import ExplanationDialog from "../../../components/misc/ExplanationDialog";

type VerdictType = components["schemas"]["Verdict"];

const VerdictSimulations: React.FC<{ onClick(buttonName: string): void }> = ({
  onClick,
}) => {
  return (
    <Card id="verdict_simulations">
      <Stack
        alignItems={"center"}
        direction={"row"}
        justifyContent={"space-between"}
      >
        <Stack spacing={1}>
          <Typography variant={"h5"}>
            {
              Translations.pages.dashboard_page.tabs.trial
                .verdict_simulations_block.title
            }
          </Typography>
          <Typography color="grey" variant={"caption"}>
            {
              Translations.pages.dashboard_page.tabs.trial
                .verdict_simulations_block.description
            }
          </Typography>
        </Stack>
        <Stack alignItems={"center"} direction={"row"} spacing={2}>
          {Translations.pages.dashboard_page.tabs.trial.verdict_simulations_block.buttons.map(
            (btn, index) => (
              <Button
                width={"auto"}
                onClick={() => onClick(btn.key)}
                color={"purple"}
                key={index}
                variant="outlined"
              >
                <Stack direction={"row"} spacing={1}>
                  <TextSnippetOutlined />
                  <Box>{btn.label}</Box>
                </Stack>
              </Button>
            )
          )}
        </Stack>
      </Stack>
    </Card>
  );
};

// const SimulationDialog: React.FC<{
//   open: boolean;
//   onClose: () => void;
//   title: string;
//   subtitle: string;
//   content: string;
// }> = ({ title, subtitle, content, open, onClose }) => {
//   return (
//     <Dialog
//       minHeight={"none"}
//       fullWidth
//       maxWidth="lg"
//       onClose={onClose}
//       open={open}
//       title={title}
//     >
//         <Typography variant={"body1"}>{content}</Typography>
//     </Dialog>
//   );
// };

const SimulationDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  content: string;
}> = ({ title, subtitle, content, open, onClose }) => {
  // Function to convert markdown to HTML
  const renderMarkdown = (text: string): string => {
    if (!text) return '';

    // Replace markdown patterns with HTML
    let html = text
      // Headers
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')

      // Bold and italic
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')

      // Links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')

      // Lists
      .replace(/^\s*\n\* (.*)/gm, '<ul>\n<li>$1</li>')
      .replace(/^\* (.*)/gm, '<li>$1</li>')
      .replace(/^\s*\n- (.*)/gm, '<ul>\n<li>$1</li>')
      .replace(/^- (.*)/gm, '<li>$1</li>')
      .replace(/^\s*\n([0-9]+)\. (.*)/gm, '<ol>\n<li>$2</li>')
      .replace(/^([0-9]+)\. (.*)/gm, '<li>$2</li>')

      // Paragraphs
      .replace(/^\s*(\n)?(.+)/gm, function(m: string): string {
        return /\<(\/)?(h|ul|ol|li|blockquote|pre|img)/.test(m) ? m : '<p>' + m + '</p>';
      })

      // Line breaks
      .replace(/\n/g, '<br />');

    return html;
  };

  return (
    <Dialog
      minHeight={"none"}
      fullWidth
      maxWidth={700}
      onClose={onClose}
      open={open}
      title={title}
      sx={{
        "& .MuiDialog-paper": {
          width: "90%",
          maxWidth: "1200px",
        },
      }}
    >
      {subtitle && <Typography variant="subtitle1">{subtitle}</Typography>}
      <Box
        sx={{
          marginTop: "16px",
          "& h1": {
            fontSize: "2em",
            fontWeight: "bold",
            marginBottom: "0.5em",
          },
          "& h2": {
            fontSize: "1.5em",
            fontWeight: "bold",
            marginBottom: "0.5em",
          },
          "& h3": {
            fontSize: "1.17em",
            fontWeight: "bold",
            marginBottom: "0.5em",
          },
          "& p": { marginBottom: "1em" },
          "& a": { color: "#1976d2", textDecoration: "none" },
          "& a:hover": { textDecoration: "underline" },
          "& ul, & ol": { paddingLeft: "2em", marginBottom: "1em" },
          "& li": { marginBottom: "0.5em" },
        }}
        dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
      />
    </Dialog>
  );
};

const Trial: React.FC = () => {
  const { currentOutcome } = useCase() || {};

  const [simulationDialogOpen, setSimulationDialogOpen] = React.useState(false);
  const [simulationTitle, setSimulationTitle] = React.useState("");
  const [simulationSubtitle, setSimulationSubtitle] = React.useState("");
  const [simulationContent, setSimulationContent] = React.useState("");
  const [isTrialExplanationOpen, setIsTrialExplanationOpen] =
    React.useState(false);
  const [isLiabilityExplanationOpen, setIsLiabilityExplanationOpen] =
    React.useState(false);

  const handleSimulationClick = (key: keyof VerdictType) => {
    // console.log("---------Verdict:", currentOutcome?.predictions?.verdict);
    // console.log("---------Selected key:", key);
    // console.log("----------Content for key:", currentOutcome?.predictions?.verdict?.[key]);

    setSimulationTitle(
      Translations.pages.dashboard_page.tabs.trial.verdict_simulations_block.buttons.find(
        (b) => b.key === key
      )?.label ?? ""
    );
    setSimulationContent(currentOutcome?.predictions?.verdict?.[key] ?? "");
    setSimulationDialogOpen(true);
  };

  return (
    <Stack spacing={2}>
      <Stack direction={"row"} spacing={2}>
        <Card
          id="trial_likelihood"
          sx={{ display: "flex", backgroundColor: "white", width: "30%" }}
        >
          <Likelihood
            type="verdict"
            start={
              currentOutcome?.predictions?.resolution_likelihood.trial
                .confidence_interval?.min ?? 0
            }
            end={
              currentOutcome?.predictions?.resolution_likelihood.trial
                .confidence_interval?.max ?? 0
            }
            icon={<GavelOutlined fontSize="large" />}
            showInfoIcon={
              !!currentOutcome?.predictions?.resolution_likelihood.trial
                .explanation
            }
            onInfoClick={() => setIsTrialExplanationOpen(true)}
            infoIconProps={{
              backgroundColor: "#5E35B1",
              fontSize: 24,
            }}
            showAsRange={true}
          />
          <ExplanationDialog
            title={
              Translations.pages.dashboard_page.tabs.trial.trial_likelihood_block.dialog.title
            }
            open={isTrialExplanationOpen}
            onClose={() => setIsTrialExplanationOpen(false)}
            explanation={
              currentOutcome?.predictions?.resolution_likelihood.trial
                .explanation || ""
            }
          />
        </Card>

        {/* Liability Likelihood Card - Show as Single Value */}
        <Card sx={{ display: "flex", backgroundColor: "white", width: "30%" }}>
          <Likelihood
            id="liability_likelihood"
            type={"defendant liability"}
            category={"Attribution"}
            start={
              currentOutcome?.predictions?.liability.probability_percent ?? 0
            }
            end={
              currentOutcome?.predictions?.liability.probability_percent ?? 0
            }
            icon={<FactCheckOutlined fontSize="large" />}
            showInfoIcon={!!currentOutcome?.predictions?.liability.explanation}
            onInfoClick={() => setIsLiabilityExplanationOpen(true)}
            infoIconProps={{
              backgroundColor: "#5E35B1",
              fontSize: 24,
            }}
            // Show as single value
            showAsRange={false}
          />
          <ExplanationDialog
          title={
              Translations.pages.dashboard_page.tabs.trial.verdict_simulations_block.title
            }
            open={isLiabilityExplanationOpen}
            onClose={() => setIsLiabilityExplanationOpen(false)}
            explanation={
              currentOutcome?.predictions?.liability.explanation || ""
            }
          />
        </Card>

        <Card
          id="party_favored"
          sx={{ backgroundColor: "white", width: "40%" }}
        >
          <Typography variant="caption" sx={{ color: "rgba(0, 0, 0, 0.56)" }}>
            {
              Translations.pages.dashboard_page.tabs.trial.party_favored_block
                .title
            }
          </Typography>
          <Stack
            width="100%"
            height="100%"
            alignItems="center" // This centers the Box
          >
            <Box
              width="90%"
              height="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <ProgressBar
                height={20}
                filledColor="#7C4DFF"
                emptyColor="#B388FF"
                progressPlacement="top"
                labels={[
                  Translations.pages.dashboard_page.tabs.trial.liability_block
                    .progress_graph.labels.plaintiff,
                  Translations.pages.dashboard_page.tabs.trial.liability_block
                    .progress_graph.labels.defendant,
                ]}
                progress={
                  currentOutcome?.predictions?.party_favored.plaintiff
                    .probability_percent ?? 0
                }
                explanations={
                  [
                    currentOutcome?.predictions?.party_favored.plaintiff
                      ?.explanation
                      ? JSON.stringify(
                          currentOutcome.predictions.party_favored.plaintiff
                            .explanation
                        ).replace(/"/g, "")
                      : "",
                    currentOutcome?.predictions?.party_favored.defendant
                      ?.explanation
                      ? JSON.stringify(
                          currentOutcome.predictions.party_favored.defendant
                            .explanation
                        ).replace(/"/g, "")
                      : "",
                  ] as [string, string]
                }
                thresholdRange={
                  currentOutcome?.predictions?.party_favored.plaintiff
                    ?.confidence_interval?.min != null &&
                  currentOutcome?.predictions?.party_favored.plaintiff
                    ?.confidence_interval?.max != null
                    ? ([
                        currentOutcome.predictions.party_favored.plaintiff
                          .confidence_interval.min,
                        currentOutcome.predictions.party_favored.plaintiff
                          .confidence_interval.max,
                      ] as [number, number])
                    : []
                }
              />
            </Box>
          </Stack>
        </Card>
      </Stack>
      <Card
        id="trial_damages"
        sx={{
          width: "100%",
          backgroundColor: "white",
          height: "100%",
          justifyContent: "space-between",
          alignItems: "center",
          display: "flex",
          gap: 2,
        }}
      >
        <Box width={"100%"}>
          <Likelihood
            icon={<AttachMoneyOutlined fontSize="large" />}
            start={`$${formatNumberWithSuffix(
              currentOutcome?.predictions?.trial_damages.min ?? 0
            )}`}
            end={`$${formatNumberWithSuffix(
              currentOutcome?.predictions?.trial_damages.max ?? 0
            )}`}
            type="verdict"
            category={
              Translations.pages.dashboard_page.tabs.trial.damages_block.title
                .bold
            }
            unit=""
          />
        </Box>
        <Box width={"100%"} textAlign={"right"}>
          <Typography variant="caption" sx={{ color: "rgba(0, 0, 0, 0.56)" }}>
            {
              Translations.pages.dashboard_page.tabs.trial.trial_damages_block
                .title
            }
          </Typography>
          {currentOutcome?.predictions && (
            <BellCurve
              width={600}
              height={250}
              data={
                currentOutcome?.predictions?.trial_damages.data_points?.map(
                  (p) => ({ x: p[0], y: p[1] })
                ) ?? []
              }
              confidenceMin={
                currentOutcome?.predictions?.trial_damages.min ?? undefined
              }
              confidenceMax={
                currentOutcome?.predictions?.trial_damages.max ?? undefined
              }
            />
          )}
        </Box>
        <Box width={"100%"}>
          <Donut
            data={[
              {
                name: Translations.pages.dashboard_page.tabs.trial
                  .economic_damages_block.items.economic.label,
                value: currentOutcome?.predictions?.trial_damages.economic ?? 0,
                color:
                  Translations.pages.dashboard_page.tabs.trial
                    .economic_damages_block.items.economic.color,
              },
              {
                name: Translations.pages.dashboard_page.tabs.trial
                  .economic_damages_block.items.noneconomic.label,
                value:
                  currentOutcome?.predictions?.trial_damages.non_economic ?? 0,
                color:
                  Translations.pages.dashboard_page.tabs.trial
                    .economic_damages_block.items.noneconomic.color,
              },
            ]}
          />
        </Box>
      </Card>
      <VerdictSimulations
        onClick={(key: keyof VerdictType) => handleSimulationClick(key)}
      />
      <SimulationDialog
        open={simulationDialogOpen}
        onClose={() => setSimulationDialogOpen(false)}
        title={simulationTitle}
        subtitle={simulationSubtitle}
        content={simulationContent}
      />
    </Stack>
  );
};

export default Trial;
